import React from "react";

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components/macro";

import { setSetting } from "actions";

import { MultiButton, SlimSecondaryButton } from "components/Button";
import { Column, Row } from "components/Layout";
import {
  ColoredFontAwesomeIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { Settings } from "constants/settings";

import { useMultipleTabDetection } from "hooks/useMultipleTabDetection";

import { TabInfo } from "lib/tabMessaging";
import { formatTime } from "lib/timeFormats";

import { getSetting } from "selectors";

// Row doesn't support border-bottom-only; extend it minimally with theme values.
const TabEntry = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray85};
`;

// Flex children need min-width: 0 for text truncation to work correctly.
const TabDetails = styled(Column)`
  min-width: 0;
`;

const TabPath = styled(Row)`
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const TabMeta = styled(Row)`
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
  color: ${({ theme }) => theme.colors.titleLight};
`;

function TabRow({ tab }: { tab: TabInfo }) {
  const { activeRole, openedAt, url, path } = tab;

  return (
    <TabEntry alignCenter paddingVertical={1} gridGap={1}>
      <ColoredFontAwesomeIcon icon={faExclamationTriangle} color="warning" />
      <TabDetails>
        <TabPath truncate>{path || "/"}</TabPath>
        <TabMeta truncate>
          Open since {formatTime(openedAt)} &middot; {url}
        </TabMeta>
        {activeRole && (
          <TabMeta truncate>
            {activeRole.username} - {activeRole.name}
          </TabMeta>
        )}
      </TabDetails>
    </TabEntry>
  );
}

function MultipleTabsModal() {
  const dispatch = useDispatch();
  const location = useLocation();
  const dismissedUntil = useSelector(
    getSetting(Settings.multipleTabsModalDismissedUntil),
  );
  const isSessionDismissed = useSelector(
    getSetting(Settings.multipleTabsModalDismissedForSession),
  );

  const isDismissed =
    isSessionDismissed || (dismissedUntil && dismissedUntil > Date.now());

  const { otherTabs, checkNow } = useMultipleTabDetection();

  const isOnWatchPage = location.pathname.endsWith("/watch");
  const isOpen = otherTabs.length > 0 && !isDismissed && !isOnWatchPage;

  const dismissForMinutes = (forMinutes: number) => {
    dispatch(
      setSetting(
        Settings.multipleTabsModalDismissedUntil,
        Date.now() + forMinutes * 60 * 1000,
      ),
    );
  };

  const dismissForSession = () => {
    dispatch(setSetting(Settings.multipleTabsModalDismissedForSession, true));
  };

  if (!isOpen) {
    return null;
  }

  const dismissButtons = [
    {
      title: "Dismiss for 30 minutes",
      onClick: () => dismissForMinutes(30),
      default: true,
    },
    {
      title: "Dismiss for 24 hours",
      onClick: () => dismissForMinutes(24 * 60),
    },
    {
      title: "Dismiss until logout",
      onClick: dismissForSession,
    },
  ];

  return (
    <Dialog open={isOpen}>
      <DialogTitle
        icon={faExclamationTriangle}
        iconColor="warning"
        onClose={undefined}
      >
        Multiple AgriNous tabs open
      </DialogTitle>
      <DialogContent dividers>
        <p>It looks like you might have AgriNous open in more than 1 tab.</p>
        <p>This can cause errors and result in data loss or missed updates.</p>

        <div>
          {otherTabs.map(tab => (
            <TabRow key={tab.id} tab={tab} />
          ))}
        </div>
        <p>
          <strong>Tip:</strong> You can run 2 roles or sales simultaneously by
          using separate browsers (eg Chrome and Firefox, or Safari and Edge).
        </p>
      </DialogContent>
      <DialogActions>
        <Row flexWrap gridGap={2}>
          <SlimSecondaryButton onClick={checkNow}>
            Check again
          </SlimSecondaryButton>

          <MultiButton buttons={dismissButtons} />
        </Row>
      </DialogActions>
    </Dialog>
  );
}

export default MultipleTabsModal;
